import { memo, useCallback, useMemo } from 'react';
import { ChakraFlex } from '@equip.health/ui';
import { getGroupedSlotsByDate } from '../utils';
import TimeSlotGroup from './TimeSlotGroup';
import TimeSlot from './TimeSlot';
import { isSlotSelected } from './util';
import VirtualizedSlotList from './VirtualizedSlotList';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';
import { ProviderAssignmentSelectionTab } from '../constants';
import Tabs from '../Tabs';

const TOP_LEVEL_ID = 'provider-assignment-time-slot-selection';

interface TimeSlotSelectionProps {
  availabilityTooltipMessage?: string;
  defaultTab?: ProviderAssignmentSelectionTab;
  isLoading: boolean;
  onSelect: (slot: ProviderAppointmentSlot) => void;
  onTabChange?: (tab: ProviderAssignmentSelectionTab) => void;
  patientAvailabilityPreferences: PatientAvailabilityPreferences | null;
  patientTimezone?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  selectedSlot?: ProviderAppointmentSlot;
  slots: ProviderAppointmentSlot[];
  tieredProviderMap: Record<
    ProviderAssignmentSelectionTab,
    Record<string, GetProviderByIdResponse>
  >;
}

const TimeSlotSelection = ({
  availabilityTooltipMessage,
  defaultTab,
  isLoading,
  onSelect,
  onTabChange,
  patientAvailabilityPreferences,
  patientTimezone,
  scrollContainerRef,
  selectedSlot,
  slots,
  tieredProviderMap,
}: TimeSlotSelectionProps) => {
  // Pre-compute the expensive grouped slots per tab. This only recalculates
  // when slots data, provider tiers, or timezone change — NOT on selection.
  const groupedSlotsByTab = useMemo(() => {
    if (isLoading || !slots?.length) return null;
    const result = {} as Record<
      ProviderAssignmentSelectionTab,
      Record<string, AppointmentSlot[]>
    >;
    for (const tab of Object.values(ProviderAssignmentSelectionTab)) {
      result[tab] = getGroupedSlotsByDate({
        patientAvailabilityPreferences,
        patientTimezone,
        providerMap: tieredProviderMap[tab],
        slots,
      });
    }
    return result;
  }, [isLoading, patientAvailabilityPreferences, patientTimezone, slots, tieredProviderMap]);

  const renderSlots = useCallback(
    (tab: ProviderAssignmentSelectionTab) => {
      if (isLoading || !groupedSlotsByTab) {
        return (
          <ChakraFlex direction="column" gap="24px">
            <SkeletonLoaderList
              id={`${TOP_LEVEL_ID}__placeholder`}
              itemHeight="64px"
              itemWidth="240px"
            />
          </ChakraFlex>
        );
      }

      const providerMap = tieredProviderMap[tab];
      const groupedSlotsByDate = groupedSlotsByTab[tab] ?? {};

      if (scrollContainerRef) {
        return (
          <VirtualizedSlotList
            groupedSlotsByDate={groupedSlotsByDate}
            id={TOP_LEVEL_ID}
            onSelect={onSelect}
            patientAvailabilityPreferences={patientAvailabilityPreferences}
            patientTimezone={patientTimezone}
            providerMap={providerMap}
            scrollContainerRef={scrollContainerRef}
            selectedSlot={selectedSlot}
          />
        );
      }

      return (
        <ChakraFlex direction="column" gap="24px">
          {Object.entries(groupedSlotsByDate)
            .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
            .map(([date, dateSlots], index) => (
              <TimeSlotGroup
                date={date}
                firstSlotStartTime={dateSlots[0]?.startTime}
                id={TOP_LEVEL_ID}
                isFirstGroup={index === 0}
                key={date}
                patientTimezone={patientTimezone}
              >
                {dateSlots.map((slot) => (
                  <TimeSlot
                    id={TOP_LEVEL_ID}
                    isSelected={isSlotSelected(slot, selectedSlot)}
                    key={`${slot.startTime}-${slot.providerId}`}
                    onSelect={onSelect}
                    patientAvailabilityPreferences={patientAvailabilityPreferences}
                    provider={providerMap[slot.providerId]}
                    slot={slot}
                  />
                ))}
              </TimeSlotGroup>
            ))}
        </ChakraFlex>
      );
    },
    [
      groupedSlotsByTab,
      isLoading,
      onSelect,
      patientAvailabilityPreferences,
      patientTimezone,
      scrollContainerRef,
      selectedSlot,
      tieredProviderMap,
    ],
  );

  return (
    <Tabs
      availabilityTooltipMessage={availabilityTooltipMessage}
      defaultTab={defaultTab}
      id={TOP_LEVEL_ID}
      onTabChange={onTabChange}
      renderTabContent={renderSlots}
      tieredProviderMap={tieredProviderMap}
    />
  );
};

export default memo(TimeSlotSelection);
